import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { User } from '@/types'
import { safeStorage } from './safeStorage'
import { clearMapView } from '@/lib/dashboardMapView'

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  _hasHydrated: boolean

  // Actions
  setUser: (user: User) => void
  setTokens: (access: string, refresh: string) => void
  login: (user: User, access: string, refresh: string) => void
  logout: () => void
  updateUser: (user: Partial<User>) => void
  setHasHydrated: (state: boolean) => void
}

/** The localStorage key every tab of this browser shares for the login (zustand persist). */
const AUTH_STORAGE_KEY = 'auth-storage'

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      _hasHydrated: false,

      setUser: (user) => set({ user }),

      setTokens: (access, refresh) =>
        set({
          accessToken: access,
          refreshToken: refresh,
        }),

      login: (user, access, refresh) => {
        clearMapView()   // #290 — a view left in this tab by someone else is never shown
        set({
          user,
          accessToken: access,
          refreshToken: refresh,
          isAuthenticated: true,
        })
      },

      logout: () => {
        clearMapView()   // #290 — the next person on this browser starts from the fleet view
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        })
      },

      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      setHasHydrated: (state) => set({ _hasHydrated: state }),
    }),
    {
      name: AUTH_STORAGE_KEY,
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      storage: createJSONStorage(() => safeStorage),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    }
  )
)

/** #334 — the access + refresh pair as stored now, and whose it is (the user id), read straight from storage (not
 *  this tab's copy in memory): what the last tab of this browser to refresh or log in wrote. Nulls when nothing
 *  readable is stored. */
export function storedTokens(): { access: string | null; refresh: string | null; userId: number | null } {
  try {
    const raw = safeStorage.getItem(AUTH_STORAGE_KEY)
    const state = raw
      ? (JSON.parse(raw) as {
          state?: { accessToken?: unknown; refreshToken?: unknown; user?: { id?: unknown } | null } | null
        } | null)?.state
      : null
    return {
      access: typeof state?.accessToken === 'string' ? state.accessToken : null,
      refresh: typeof state?.refreshToken === 'string' ? state.refreshToken : null,
      userId: typeof state?.user?.id === 'number' ? state.user.id : null,
    }
  } catch {
    return { access: null, refresh: null, userId: null }
  }
}

// #334 — one login per browser. Another tab rotated the tokens, logged out or logged in: read the stored copy in
// (the displayPrefsStore pattern), so this tab never sends a refresh token the server already blacklisted, and a
// logout or a login in one tab reaches the others at once. Decided from what is stored when the event is handled,
// not from the event (the copy may have been set again since): nothing stored logs this tab out (a rehydrate keeps
// the state in memory when nothing is stored). Fix round 1 (review I2): when the stored login is another user's
// and this tab never saw the logout in between (a frozen tab resuming), the page starts over as that user (empty
// cache, fresh socket) instead of showing one user's data under another's session.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== AUTH_STORAGE_KEY) return
    if (safeStorage.getItem(AUTH_STORAGE_KEY) === null) {
      useAuthStore.getState().logout()
      return
    }
    const before = useAuthStore.getState().user?.id
    void useAuthStore.persist.rehydrate()
    const after = useAuthStore.getState().user?.id
    if (before != null && after != null && before !== after) window.location.assign('/')
  })
}
